import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID.ts';
import {
	CustomDetailCategory,
	DataCustomDetailTct,
	DataFileTct,
} from '@dealstack/shared/lib/generated';
import { useState } from 'react';
import type {
	KycPlacementDraft,
	KycPlacementValue,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';

export enum CREATE_KYC_DOCUMENT_FORM_FIELDS {
	name = 'name',
	instructions = 'instructions',
	category = 'category',
	requiresSignature = 'requiresSignature',
	useManualSignaturePlacement = 'useManualSignaturePlacement',
}

const editKycDocumentFormShape = z.object({
	[CREATE_KYC_DOCUMENT_FORM_FIELDS.name]: z.string().nonempty(),
	[CREATE_KYC_DOCUMENT_FORM_FIELDS.instructions]: z.string(),
	[CREATE_KYC_DOCUMENT_FORM_FIELDS.category]: z.string().nullable().optional(),
	[CREATE_KYC_DOCUMENT_FORM_FIELDS.requiresSignature]: z.boolean(),
	[CREATE_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement]: z.boolean(),
});

export type EditKycDocumentFormValues = z.infer<typeof editKycDocumentFormShape>;

export const useCreateKycDocumentDrawerController = ({
	file,
	type,
	addFormTemplate,
	category,
	onClose,
}: {
	file: DataFileTct;
	type: DataCustomDetailTct['type'];
	category?: CustomDetailCategory | null;
	addFormTemplate: (formTemplate: FormTemplate) => void;
	onClose: () => void;
}) => {
	const editKycDocumentForm = useForm<EditKycDocumentFormValues>({
		mode: 'all',
		defaultValues: {
			[CREATE_KYC_DOCUMENT_FORM_FIELDS.name]: '',
			[CREATE_KYC_DOCUMENT_FORM_FIELDS.instructions]: '',
			[CREATE_KYC_DOCUMENT_FORM_FIELDS.category]: category || null,
			[CREATE_KYC_DOCUMENT_FORM_FIELDS.requiresSignature]: false,
			[CREATE_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement]: false,
		},
		resolver: zodResolver(editKycDocumentFormShape),
	});

	// The template doesn't exist yet, so the box is held here and sent with the create call.
	const [pendingPlacement, setPendingPlacement] = useState<KycPlacementValue | null>(null);
	// Held alongside the box: it decides the box's aspect ratio, so sending one without the
	// other makes the server validate the geometry against the wrong ratio.
	const [showStampMetadata, setShowStampMetadata] = useState(true);

	const onPlacementSaved = (draft: KycPlacementDraft) => {
		setPendingPlacement(draft.placement);
		setShowStampMetadata(draft.showStampMetadata);
		editKycDocumentForm.setValue(
			CREATE_KYC_DOCUMENT_FORM_FIELDS.useManualSignaturePlacement,
			draft.useManualSignaturePlacement
		);
	};

	const onSubmit = (formValues: EditKycDocumentFormValues) => {
		const useManualSignaturePlacement =
			formValues.requiresSignature && formValues.useManualSignaturePlacement;
		const formTemplateToCreate: FormTemplate = {
			id: generateUUID(),
			local: true,
			name: formValues.name,
			instruction: formValues.instructions,
			type,
			fileId: file.id,
			fileName: file.name,
			category: category || undefined,
			requiresSignature: formValues.requiresSignature,
			useManualSignaturePlacement,
			showStampMetadata,
			pendingPlacement: useManualSignaturePlacement ? pendingPlacement : null,
		};

		addFormTemplate(formTemplateToCreate);
		onClose();
	};

	return {
		editKycDocumentForm,
		onSubmit,
		onPlacementSaved,
		pendingPlacement,
		showStampMetadata,
	};
};
